import {NumberInput} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {produce} from 'immer';
import React from 'react';
import {OptionListEditor} from 'src/design/molecules/OptionListEditor';
import type {OptionListEditorItem} from 'src/design/molecules/OptionListEditor';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  hasInvalidMeasurementUnits,
  isMeasurementUnitInvalid,
} from '../../../utils/measurementValidation';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

/**
 * Measurement field settings: units add/edit/remove (via `OptionListEditor`,
 * treating each unit's array index as a stable id since units are plain
 * strings), min/max value, and decimal places. Self-filters on
 * `field.type === 'measurement'`.
 */
export const MeasurementSettings = React.memo(() => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();

  const units = field?.measurementOptions?.units ?? [];
  const invalidUnits = hasInvalidMeasurementUnits(units);

  const patchMeasurement = (
    patch: Partial<NonNullable<IFormFieldV2['measurementOptions']>>
  ) => {
    if (!field) return;
    updateSelectedField(
      produce(field, draft => {
        draft.measurementOptions = {...draft.measurementOptions, ...patch};
      })
    );
  };

  const handleAddUnit = () => {
    if (invalidUnits) return;
    patchMeasurement({units: [...units, '']});
  };

  const handleUnitChange = (id: string, value: string) => {
    const index = Number(id);
    const nextUnits = [...units];
    nextUnits[index] = value;
    patchMeasurement({units: nextUnits});
  };

  const handleRemoveUnit = (id: string) => {
    const index = Number(id);
    patchMeasurement({units: units.filter((_, i) => i !== index)});
  };

  const handleReorderUnits = (sourceIndex: number, destinationIndex: number) => {
    const nextUnits = [...units];
    const [moved] = nextUnits.splice(sourceIndex, 1);
    nextUnits.splice(destinationIndex, 0, moved);
    patchMeasurement({units: nextUnits});
  };

  if (!field || field.type !== 'measurement') return null;

  const items: OptionListEditorItem[] = units.map((unit, index) => ({
    id: String(index),
    value: unit,
  }));

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Units
        </Text>
        <OptionListEditor
          items={items}
          onAdd={handleAddUnit}
          onChange={handleUnitChange}
          onRemove={handleRemoveUnit}
          onReorder={handleReorderUnits}
          addLabel="Add Unit"
          valuePlaceholder="e.g. psi"
          addDisabled={invalidUnits}
          itemError={id => isMeasurementUnitInvalid(units, Number(id))}
          emptyMessage="At least one unit is required."
        />
        {units.length > 0 && invalidUnits && (
          <Text size="xs" c="red">
            Units must be non-empty and unique.
          </Text>
        )}
      </Stack>

      <Group grow>
        <NumberInput
          label="Minimum Value"
          value={field.measurementOptions?.minValue}
          onChange={val =>
            patchMeasurement({
              minValue: typeof val === 'number' ? val : undefined,
            })
          }
        />
        <NumberInput
          label="Maximum Value"
          value={field.measurementOptions?.maxValue}
          onChange={val =>
            patchMeasurement({
              maxValue: typeof val === 'number' ? val : undefined,
            })
          }
        />
      </Group>

      <NumberInput
        label="Decimal Places"
        value={field.measurementOptions?.decimalPlaces}
        onChange={val =>
          patchMeasurement({
            decimalPlaces: typeof val === 'number' ? val : undefined,
          })
        }
        min={0}
        max={6}
      />
    </Stack>
  );
});
